import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, ArrowRight } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberDevice: false,
    rememberDays: '7',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login({
        username: formData.identifier,
        password: formData.password,
        remember_device: formData.rememberDevice,
        remember_days: formData.rememberDevice ? Number(formData.rememberDays) : null,
      });
      toast.success('Login successful!');
      // Redirect based on role - agents go to packages, admins go to dashboard
      if (userData.role === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/packages');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" data-testid="login-home-link" className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                S
              </div>
              <div className="leading-none">
                <span className="block text-xl font-bold text-slate-900">Suvidha</span>
                <span className="block text-xs font-semibold text-primary tracking-wider uppercase">Travel</span>
              </div>
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              Welcome Back
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Sign In</h1>
            <p className="text-slate-500">Access your B2B dashboard and bookings.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email or username</Label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="identifier"
                  data-testid="login-email-or-username"
                  placeholder="Enter your email or username"
                  className="pl-10 h-12"
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="remember-device-controls">
              <label htmlFor="remember-device" className="flex cursor-pointer items-start gap-3">
                <input
                  id="remember-device"
                  type="checkbox"
                  checked={formData.rememberDevice}
                  onChange={(event) => setFormData({ ...formData, rememberDevice: event.target.checked })}
                  data-testid="remember-device-checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span><span className="block text-sm font-semibold text-slate-700">Remember this device</span><span className="mt-0.5 block text-xs leading-relaxed text-slate-500">Only use this on a personal or trusted device.</span></span>
              </label>
              {formData.rememberDevice && (
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <Label htmlFor="remember-days" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Keep me signed in for</Label>
                  <select id="remember-days" value={formData.rememberDays} onChange={(event) => setFormData({ ...formData, rememberDays: event.target.value })} data-testid="remember-device-duration" className="mt-2 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition-colors duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20">
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                  </select>
                  <p className="mt-2 text-xs text-slate-500" data-testid="remember-device-expiry-note">You will stay signed in for {formData.rememberDays} days unless you sign out.</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  placeholder="Enter your password"
                  className="pl-10 h-12"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="animate-spin mr-2" size={20} />
              ) : (
                <>Sign In <ArrowRight size={18} className="ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-sm text-slate-400 hover:text-primary transition-colors">
              Forgot Password?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-slate-500">
              New to Suvidha?{' '}
              <Link to="/register" data-testid="login-register-link" className="text-primary font-semibold hover:underline">
                Register B2B Account
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/2007395/pexels-photo-2007395.jpeg"
          alt="Travel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-slate-900/90"></div>
        <div className="relative z-10 flex flex-col justify-center p-16 text-white">
          <h2 className="text-4xl font-bold mb-4">Grow Your Travel Business</h2>
          <p className="text-lg text-white/80 mb-8 max-w-md">
            Access wholesale rates for India's most iconic destinations. We handle the logistics, you grow your agency.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">✓</div>
              <span>Direct B2B Nett Rates</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">✓</div>
              <span>Instant Invoice Generation</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">✓</div>
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
